import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../types';
import Message from './Message';
import StreamProgress from './StreamProgress';
import type { StreamPhase } from './StreamProgress';
import { DomainExplorer } from './AuthGate';

interface Props {
  messages: ChatMessage[];
  isStreaming: boolean;
  onCitationClick: (msgId: string, num: number) => void;
  onShowMobileSources: (msgId: string) => void;
  onFollowUp?: (query: string) => void;
  onFillInput?: (text: string) => void;
  streamPhase?: StreamPhase;
  onBookmarkChange?: () => void;
  /** Phase 8: plain english mode active */
  plainEnglish?: boolean;
  /** Highlight refresh key — bumped when highlights change */
  highlightRefreshKey?: number;
  /** Called when a highlight is removed via overlay */
  onHighlightRemoved?: () => void;
  /** Send handler (used by follow-up) */
  onSend?: (query: string) => void;
}

export default function Chat({
  messages,
  isStreaming,
  onCitationClick,
  onShowMobileSources,
  onFollowUp,
  onFillInput,
  streamPhase = 'idle',
  onBookmarkChange,
  plainEnglish = false,
  highlightRefreshKey = 0,
  onHighlightRemoved,
  onSend,
}: Props) {
  const bottomRef = useRef<HTMLDivElement>(null);

  /* Auto-scroll to bottom when messages change or while streaming */
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const isEmpty = messages.length === 0;

  return (
    <>
      {isEmpty ? (
        /* ── Empty state — domain explorer with sample questions ── */
        <div className="empty-state empty-state--explorer" data-tour="citation-badges">
          <h1 className="empty-state-title">What do you need to check?</h1>
          <p className="empty-state-subtitle">
            Ask any compliance question, or pick a domain below to see examples.
          </p>
          <DomainExplorer onQuestionClick={onFillInput} />
        </div>
      ) : (
        /* ── Messages ── */
        <>
        {plainEnglish && (
          <div className="plain-english-banner" role="alert" aria-live="polite">
            ⚠️ Plain English mode — This answer simplifies technical language for readability. Always verify critical decisions against the cited clause text in Sources.
          </div>
        )}
        <div className="messages" role="log" aria-label="Conversation" data-tour="citation-badges">
          {(() => {
            // Find index of last completed (non-streaming) assistant message
            let lastCompletedAssistantIdx = -1;
            for (let i = messages.length - 1; i >= 0; i--) {
              if (messages[i].role === 'assistant' && !messages[i].isStreaming) {
                lastCompletedAssistantIdx = i;
                break;
              }
            }
            return messages.map((msg, idx) => {
              // Find the preceding user message to pass as queryText
              const queryText = msg.role === 'assistant' && idx > 0 && messages[idx - 1].role === 'user'
                ? messages[idx - 1].content
                : undefined;
              return (
                <Message
                  key={msg.id}
                  message={msg}
                  onCitationClick={onCitationClick}
                  onShowSources={
                    msg.role === 'assistant' && msg.citations.length > 0
                      ? () => onShowMobileSources(msg.id)
                      : undefined
                  }
                  isLast={idx === lastCompletedAssistantIdx}
                  onFollowUp={onFollowUp}
                  queryText={queryText}
                  onBookmarkChange={onBookmarkChange}
                  highlightRefreshKey={highlightRefreshKey}
                  onHighlightRemoved={onHighlightRemoved}
                />
              );
            });
          })()}
          {/* Stream progress indicator — shown between send and first text chunk */}
          {isStreaming && streamPhase !== 'streaming' && streamPhase !== 'idle' && (
            <StreamProgress phase={streamPhase} />
          )}
          <div ref={bottomRef} aria-hidden="true" />
        </div>
        </>
      )}
    </>
  );
}
